import { z } from 'zod';
/* ==========================================================================
 *  Food database contract (ISSUE-014, D-11)
 *
 *  Canonical, provider-agnostic food shape. USDA FoodData Central and Open
 *  Food Facts each map *into* this shape behind a `FoodAdapter` (see
 *  ../contracts/food.ts). Nothing downstream — the extraction pipeline
 *  (ISSUE-021), the ledger (ISSUE-026), the chip UI (ISSUE-024) — may know
 *  which provider a food came from beyond the `source` tag.
 *
 *  These are CONTRACT TYPES only: zod is the single source of truth and the
 *  only runtime dependency. No HTTP clients, no provider SDKs, no secrets ever
 *  live in @nutians/shared (it is bundled into the browser).
 * ========================================================================== */
/** Where a canonical food originated. `cache` is a storage location, not a source —
 *  a cached row keeps its original `source`. `user_override` is the per-user
 *  correction layer (ISSUE-025). */
export const foodSourceSchema = z.enum(['usda_fdc', 'off', 'user_override']);
/** Macros are quoted per 100 units of the food's base measure. Mass-based foods
 *  use `per_100g`; liquids use `per_100ml`. Count-based foods ("1 egg") carry a
 *  `householdServings` mapping to grams (see below). */
export const macroBasisSchema = z.enum(['per_100g', 'per_100ml']);
/** The five macros the domain persists everywhere (mirror `target` columns).
 *  Optional fields are commonly present in OFF/USDA and useful for reports, but
 *  never required — adapters set them to null when upstream omits them. */
export const macroSetSchema = z.object({
    kcal: z.number().nonnegative(),
    proteinG: z.number().nonnegative(),
    carbsG: z.number().nonnegative(),
    fatG: z.number().nonnegative(),
    fiberG: z.number().nonnegative(),
    // Optional extended nutrients (nullable, default null).
    sugarG: z.number().nonnegative().nullable().default(null),
    saturatedFatG: z.number().nonnegative().nullable().default(null),
    sodiumMg: z.number().nonnegative().nullable().default(null),
    // T13.2 — staged micronutrients from USDA/OFF where data exists.
    potassiumMg: z.number().nonnegative().nullable().default(null),
    calciumMg: z.number().nonnegative().nullable().default(null),
    ironMg: z.number().nonnegative().nullable().default(null),
    magnesiumMg: z.number().nonnegative().nullable().default(null),
    vitaminDUg: z.number().nonnegative().nullable().default(null),
    vitaminB12Ug: z.number().nonnegative().nullable().default(null),
});
/**
 * Aggregated micronutrient block for a day (T13.2 / ISSUE-076).
 *
 * All 10 micros in one place — null per-field means no upstream data
 * was available for any resolved entry on that day ("—" in the UI,
 * never zero). Summed over resolved entries only (unresolved excluded).
 *
 * Data-coverage note (USDA FDC reliably: all 10; OFF: fiber/sugar/sodium
 * usually present, potassium/calcium/iron sometimes, vitamin D/B12 rare).
 */
export const microSetSchema = z.object({
    fiberG: z.number().nonnegative().nullable(),
    sugarG: z.number().nonnegative().nullable(),
    saturatedFatG: z.number().nonnegative().nullable(),
    sodiumMg: z.number().nonnegative().nullable(),
    potassiumMg: z.number().nonnegative().nullable(),
    calciumMg: z.number().nonnegative().nullable(),
    ironMg: z.number().nonnegative().nullable(),
    magnesiumMg: z.number().nonnegative().nullable(),
    vitaminDUg: z.number().nonnegative().nullable(),
    vitaminB12Ug: z.number().nonnegative().nullable(),
});
/** Zero-value placeholder for the 6 new micro fields — spread into MacroSet
 *  object literals where upstream data is unavailable (e.g. user_stated entries,
 *  test fixtures). All fields typed as `number | null` to satisfy MacroSet. */
export const NULL_MICRO_FIELDS = {
    potassiumMg: null,
    calciumMg: null,
    ironMg: null,
    magnesiumMg: null,
    vitaminDUg: null,
    vitaminB12Ug: null,
};
/**
 * WHO/EU reference daily values for display comparison (T13.2 / ISSUE-076).
 * Additive framing only — never shown as "you're deficient/bad".
 * null = no standalone RDA (expressed as % kcal or not individually specified).
 */
export const MICRO_DAILY_REFERENCES = {
    fiberG: 25, // WHO 25 g/day
    sugarG: null, // no standalone WHO RDA for total sugars
    saturatedFatG: null, // expressed as % kcal; no standalone RDA
    sodiumMg: 2000, // WHO 2 g sodium/day
    potassiumMg: 3500, // WHO 3 500 mg/day
    calciumMg: 1000, // EU RDA 1 000 mg/day
    ironMg: 14, // EU RDA average (men 8 mg, women 18 mg)
    magnesiumMg: 375, // EU RDA 375 mg/day
    vitaminDUg: 15, // WHO 15 mcg (600 IU)/day
    vitaminB12Ug: 2.4, // WHO 2.4 mcg/day
};
/** Deterministic macro estimation — the core of pipeline stage 4 (FR-5.4).
 *  Scales a per-100 macro set to the ABSOLUTE totals for a portion measured in
 *  the food's base unit (grams for `per_100g`, ml for `per_100ml`). Pure and
 *  unit-tested so the ledger's arithmetic never depends on the AI layer.
 *  Rounds to 2 dp; nullable extended nutrients stay null when upstream omits
 *  them. */
export function scaleMacros(per100, amount) {
    const factor = amount / 100;
    const round = (n) => Math.round(n * factor * 100) / 100;
    const roundN = (n) => (n === null ? null : Math.round(n * factor * 100) / 100);
    return {
        kcal: round(per100.kcal),
        proteinG: round(per100.proteinG),
        carbsG: round(per100.carbsG),
        fatG: round(per100.fatG),
        fiberG: round(per100.fiberG),
        sugarG: roundN(per100.sugarG),
        saturatedFatG: roundN(per100.saturatedFatG),
        sodiumMg: roundN(per100.sodiumMg),
        potassiumMg: roundN(per100.potassiumMg ?? null),
        calciumMg: roundN(per100.calciumMg ?? null),
        ironMg: roundN(per100.ironMg ?? null),
        magnesiumMg: roundN(per100.magnesiumMg ?? null),
        vitaminDUg: roundN(per100.vitaminDUg ?? null),
        vitaminB12Ug: roundN(per100.vitaminB12Ug ?? null),
    };
}
/** A named real-world portion mapped to grams, so extraction can resolve
 *  "a banana" → 118 g without the user typing a weight. */
export const householdServingSchema = z.object({
    label: z.string().min(1).max(120), // "1 medium", "1 cup", "1 slice"
    grams: z.number().positive(),
});
/**
 * The canonical food record. `id` is a deterministic composite
 * (`${source}:${sourceId}`) so it doubles as the cache key in the `food` table.
 */
export const canonicalFoodSchema = z.object({
    id: z.string(), // e.g. "usda_fdc:173944"
    source: foodSourceSchema,
    sourceId: z.string(), // native id at the provider
    name: z.string().min(1),
    brand: z.string().nullable().default(null), // OFF branded items; null for whole foods
    basis: macroBasisSchema,
    macros: macroSetSchema,
    householdServings: z.array(householdServingSchema).default([]),
    /** Locale/region of the source record where known (OFF is multi-locale). */
    locale: z.string().nullable().default(null),
    /** When this record was fetched from upstream (drives weekly backfill staleness). */
    fetchedAt: z.string().datetime().nullable().default(null),
});
/** A search hit: a canonical food plus the match confidence for THIS query.
 *  Confidence is normalized 0–1 across providers so the resolver can rank
 *  cross-source candidates on one scale. */
export const foodSearchResultSchema = z.object({
    food: canonicalFoodSchema,
    confidence: z.number().min(0).max(1),
});
/** Options accepted by `FoodAdapter.search`. */
export const foodSearchOptionsSchema = z.object({
    limit: z.number().int().positive().max(50).default(10),
    /** BCP-47 region hint; OFF honors it, USDA ignores it. */
    locale: z.string().optional(),
    /** When true, adapters reject partial-name hits (OFF exact-match mode for free tier). */
    exact: z.boolean().optional(),
    /** Prefer generic/basic foods over branded, restaurant, or regional dishes. */
    preferGeneric: z.boolean().optional(),
    /** Terms that must be represented by a winning/clarification candidate. */
    requiredTerms: z.array(z.string().min(1)).optional(),
});
/* ----- Resolver output (FoodResolver, ISSUE-014 / ISSUE-021) -------------- */
export const resolutionStatusSchema = z.enum(['resolved', 'ambiguous', 'unresolved']);
/**
 * Result of resolving one free-text food mention.
 * - `resolved`   → `food` set, `confidence` high, used directly.
 * - `ambiguous`  → `candidates` set, UI/AI must disambiguate (editable chip).
 * - `unresolved` → neither; caller persists the raw mention for later re-extraction.
 *
 * `resolvedBy` is set when status='resolved', recording which link in the resolver
 * chain produced the result (ADR-0013 §5.3). Null for ambiguous/unresolved.
 * Uses a local enum (not imported from pipeline.ts) to avoid a circular dependency.
 */
export const resolvedByFoodSchema = z.enum(['user_override', 'open_food_facts', 'usda']);
export const resolvedFoodSchema = z.object({
    status: resolutionStatusSchema,
    query: z.string(),
    food: canonicalFoodSchema.nullable().default(null),
    candidates: z.array(foodSearchResultSchema).default([]),
    confidence: z.number().min(0).max(1).default(0),
    /** Resolution provenance — set when status='resolved' (ADR-0013 §5.3). */
    resolvedBy: resolvedByFoodSchema.nullable().optional(),
    /** Internal telemetry: generic-first reranking changed the top candidate. */
    genericBiasHits: z.number().int().nonnegative().optional(),
});
/* ----- Barcode lookup (GET /me/foods/barcode/:code) ----------------------- */
/**
 * Product resolved from a barcode scan. `source` indicates whether the record
 * was served from the local food cache, fetched live from OFF, or supplied by
 * the user as a per-user override. `foodId` is the stable catalogue id used
 * when creating an entry via POST /me/entries.
 */
export const barcodeProductSchema = z.object({
    barcode: z.string(),
    foodId: z.string(),
    name: z.string(),
    brand: z.string().nullable(),
    basis: macroBasisSchema,
    macros: macroSetSchema,
    householdServings: z.array(householdServingSchema),
    source: z.enum(['off', 'cache', 'user_override']),
});
//# sourceMappingURL=food.js.map